import { Descriptions } from "antd";
import { FC, useEffect, useState } from "react";
import Button from "../../Button";
import { useTranslation } from "react-i18next";
import useAuth from "../../../hooks/useAuth";
import { useHistory, useLocation } from "react-router-dom";
import { getPublisherGradeCodes, qsParse } from "../../../lib/util";
import SelectSchoolModal from "./modals/SelectSchoolModal";
import { IPostPublisherReq, IPublisher, ISchool } from "../../../models/publishers.model";
import PublisherSelect from "./PublisherSelect";
import QueryString from "qs";
import useNotification from "../../../hooks/useNotification";
import useAxios from "../../../hooks/useAxios";
import { deletePublisher, postPublisher, putPublisher } from "../../../api/publishers.api";
import useLoader from "../../../hooks/useLoader";

interface IProps {
  data?: IPublisher;
};

const PublisherForm: FC<IProps> = ({
  data,
}) => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const query = qsParse(location.search);
  const { infoNotification } = useNotification();
  const { data: postRes, isLoading: postIsLoading, error: postError, execute: postExecute } = useAxios();
  const { data: putRes, isLoading: putIsLoading, error: putError, execute: putExecute } = useAxios();
  const { data: deleteRes, isLoading: deleteIsLoading, error: deleteError, execute: deleteExecute } = useAxios();
  useLoader(
    postIsLoading || putIsLoading || deleteIsLoading,
    [postError, putError, deleteError],
  );

  const [school, setSchool] = useState<ISchool>();
  const [publisherCodes, setPublisherCodes] = useState<(string|undefined)[]>([]);
  const [selectSchoolVisible, setSelectSchoolVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    if (data) {
      setSchool({
        schoolCode: data.schoolCode,
        schoolGbn: data.schoolGbn,
        schoolKorName: data.schoolKorName,
        schoolEngName: data.schoolEngName,
        address: data.address,
        registerYn: 'Y',
      });
      const gradeCodes = getPublisherGradeCodes(data.schoolGbn);
      const newPublisherCodes: (string|undefined)[] = [];
      gradeCodes.forEach(gradeCode => {
        const publisher = data.publisherDetailResDtoList.find(item => item.gradeCode === gradeCode);
        newPublisherCodes.push(publisher?.publisherCode);
      });
      setPublisherCodes(newPublisherCodes);
    } else {
      setPublisherCodes([undefined, undefined, undefined]);
    }
  }, [data]);

  useEffect(() => {
    if (postRes || deleteRes) {
      history.push('/system/publisher');
    }
  }, [postRes, deleteRes]);

  useEffect(() => {
    if (putRes) {
      history.push(`/system/publisher?${QueryString.stringify(query)}`);
    }
  }, [putRes]);

  const onSetPublisherCode = (index: number) => (publisherCode: string) => {
    const newPublisherCodes = [...publisherCodes];
    newPublisherCodes[index] = publisherCode;
    setPublisherCodes(newPublisherCodes);
  };

  const onSubmit = () => {
    if (!school) {
      return infoNotification(t('student.student.94'));
    }
    
    const gradeCodes = getPublisherGradeCodes(school.schoolGbn);
    const req: IPostPublisherReq = {
      schoolCode: school.schoolCode,
      publisherGradeReqDtoList: [],
    };
    gradeCodes.forEach((grade, index) => {
      if (publisherCodes[index]) {
        req.publisherGradeReqDtoList.push({
          gradeCode: grade,
          publisherCode: publisherCodes[index]!,
        });
      }
    });

    if (!req.publisherGradeReqDtoList.length) {
      return infoNotification(t('student.student.95'));
    }

    if (data?.schoolCode) {
      putExecute(putPublisher(
        data.schoolCode,
        req.publisherGradeReqDtoList,
      ));
    } else {
      postExecute(postPublisher(req));
    }
  };

  const onDelete = () => {
    deleteExecute(deletePublisher(data!.schoolCode!));
  };

  return (
    <div className="main max-w1200p">
      <Descriptions
        size="small"
        column={1}
        bordered
      >
        <Descriptions.Item label={`* ${t('student.student.12')}`}>
          <Button
            className="btn btn-success btn-w150p"
            onClick={() => setSelectSchoolVisible(true)}
            text={t('system.publisher.1')}
          />
          {school && (
            <span className="ml-2">
              {school.schoolKorName}{' '}
              ({school.address})
            </span>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('system.publisher.2')}>
          <PublisherSelect
            publisherCode={publisherCodes[0]}
            setPublisherCode={onSetPublisherCode(0)}
            disabled={!!data?.schoolCode && !isEdit}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('system.publisher.3')}>
          <PublisherSelect
            publisherCode={publisherCodes[1]}
            setPublisherCode={onSetPublisherCode(1)}
            disabled={!!data?.schoolCode && !isEdit}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('system.publisher.4')}>
          <PublisherSelect
            publisherCode={publisherCodes[2]}
            setPublisherCode={onSetPublisherCode(2)}
            disabled={!!data?.schoolCode && !isEdit}
          />
        </Descriptions.Item>        
      </Descriptions>
      <div className="mt-4 flex flex-justify-between">
        <Button
          onClick={() => {
            history.push(`/system/publisher?${QueryString.stringify(query)}`);
          }}
          className="btn btn-sm btn-secondary btn-w100p mr-1"
          text={t('common.button.list')}
          writeAuthYn={writeAuthYn}
        />
        <div>
          {
            data?.schoolCode
              ? (
                <>
                  <Button
                    onClick={onDelete}
                    className="btn btn-sm btn-info btn-w100p"
                    text={t('common.button.delete')}
                  />
                  <Button
                    onClick={() => {
                      isEdit ? onSubmit() : setIsEdit(true)
                    }}
                    className="btn btn-sm btn-primary btn-w100p ml-3"
                    text={isEdit ? t('common.button.save') : t('common.button.edit')}
                  />
                </>
                )
              : (
                <Button
                  onClick={onSubmit}
                  className="btn btn-sm btn-primary btn-w100p ml-3"
                  text={t('common.button.save')}
                />
                )
          }
        </div>
      </div>

      {selectSchoolVisible && (
        <SelectSchoolModal
          visible={selectSchoolVisible}
          setVisible={setSelectSchoolVisible}
          setSchool={setSchool}
        />
      )}
    </div>
  );
};

export default PublisherForm;